import CloseIcon from "@mui/icons-material/Close";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import { Box, Button, Dialog, IconButton, TextField, Typography } from "@mui/material";
import { useEffect, useId, useState } from "react";
import { RESUME_MODAL } from "@/constants/campaignLifecycle";
import type { Campaign } from "@/interfaces/campaing";
import { formatDate } from "@/lib/campaigns";
import { resumeAsksSendDate } from "@/utils/campaign-gating-helper";

interface ResumeCampaignModalProps {
  open: boolean;
  campaign: Pick<
    Campaign,
    "name" | "notificationMode" | "pausedFrom" | "startDate" | "endDate" | "distribution"
  >;
  submitting?: boolean;
  onClose: () => void;
  onConfirm: (dates: { scheduledDate?: string; expireDate: string }) => void;
}

function toDateInput(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function addDays(n: number): Date {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + n);
  return d;
}
function day(iso: string): string {
  return iso.slice(0, 10);
}

const LABEL_SX = {
  fontFamily: "var(--font-lato)",
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: "var(--color-rodeo-black)",
} as const;

const BTN_BASE = {
  fontFamily: "var(--font-lato)",
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  borderRadius: "6px",
  px: "20px",
  py: "10px",
  minWidth: "unset",
  lineHeight: 1,
} as const;

/**
 * The forced "Set new dates to resume" picker — opened ONLY when the send date and/or expiry lapsed
 * while the campaign was paused (the server 422s a plain resume). A paused-from-scheduled
 * single-date campaign still has a send date to re-pick (multi-date has none); a paused-from-active one already sent, so only its expiry is
 * asked. Sends plain calendar days (YYYY-MM-DD); the server converts them in the campaign zone. Validates client-side and stays open on error; `onConfirm` fires only on a valid submit.
 * Styled to the prototype's `.bc-modal` (left header + close, wax callout, left-aligned footer).
 */
export default function ResumeCampaignModal({
  open,
  campaign,
  submitting = false,
  onClose,
  onConfirm,
}: ResumeCampaignModalProps) {
  const titleId = useId();
  const isManual = campaign.notificationMode === "manual";
  const hasSendDate = resumeAsksSendDate(campaign);

  const today = toDateInput(new Date());
  const tomorrow = toDateInput(addDays(1));
  const minExpiry = toDateInput(addDays(2));

  const [sendDate, setSendDate] = useState("");
  const [expireDate, setExpireDate] = useState("");
  const [error, setError] = useState("");

  // Seed sensible future defaults (prototype: send +7d, expiry +67d) each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setSendDate(toDateInput(addDays(7)));
    setExpireDate(toDateInput(addDays(67)));
    setError("");
  }, [open]);

  const reasons: string[] = [];
  if (hasSendDate && day(campaign.startDate) < today) {
    reasons.push(
      isManual
        ? RESUME_MODAL.reasonActivationPassed(formatDate(campaign.startDate))
        : RESUME_MODAL.reasonSendPassed(formatDate(campaign.startDate)),
    );
  }
  if (day(campaign.endDate) < today) {
    reasons.push(RESUME_MODAL.reasonExpired(formatDate(campaign.endDate)));
  }

  function handleSubmit() {
    if (hasSendDate && (!sendDate || sendDate <= today)) {
      setError(RESUME_MODAL.errSendFuture);
      return;
    }
    if (!expireDate) {
      setError(RESUME_MODAL.errExpiryRequired);
      return;
    }
    const sendRef = hasSendDate ? sendDate : today;
    if (expireDate <= sendRef) {
      setError(RESUME_MODAL.errExpiryAfterSend);
      return;
    }
    onConfirm({ scheduledDate: hasSendDate ? sendDate : undefined, expireDate });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      disableScrollLock
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "12px",
            p: "32px",
            m: 2,
            width: 480,
            maxWidth: "calc(100vw - 32px)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography
              id={titleId}
              component="h2"
              sx={{
                fontFamily: "var(--font-crimson)",
                fontSize: "24px",
                fontWeight: 400,
                color: "var(--color-rodeo-black)",
                m: 0,
              }}
            >
              {RESUME_MODAL.title}
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "var(--color-graphite)", mt: "2px" }}>
              {campaign.name}
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            size="small"
            sx={{ color: "var(--color-graphite)", mt: "-4px", mr: "-4px" }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: "10px",
            bgcolor: "var(--color-wax)",
            borderRadius: "10px",
            p: "12px 14px",
          }}
        >
          <EventRepeatIcon sx={{ fontSize: 20, color: "var(--text-dust-ink)", flexShrink: 0 }} />
          <Typography
            sx={{ fontSize: "13.5px", color: "var(--color-rodeo-black)", lineHeight: 1.5 }}
          >
            {RESUME_MODAL.calloutLead}
            {reasons.join(" and ")}
            {RESUME_MODAL.calloutTail}
          </Typography>
        </Box>

        {error && (
          <Box
            sx={{
              bgcolor: "var(--color-good-wine-bg)",
              border: "1px solid var(--color-good-wine-4)",
              borderRadius: "8px",
              p: "10px 12px",
            }}
          >
            <Typography sx={{ fontSize: "13.5px", color: "var(--color-good-wine)" }}>
              {error}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", gap: "12px" }}>
          {hasSendDate && (
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
              <Typography component="label" sx={LABEL_SX}>
                {isManual ? RESUME_MODAL.activationDateLabel : RESUME_MODAL.sendDateLabel}
              </Typography>
              <TextField
                type="date"
                size="small"
                value={sendDate}
                onChange={(e) => setSendDate(e.target.value)}
                slotProps={{ htmlInput: { min: tomorrow } }}
              />
            </Box>
          )}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
            <Typography component="label" sx={LABEL_SX}>
              {RESUME_MODAL.expirationDateLabel}
            </Typography>
            <TextField
              type="date"
              size="small"
              value={expireDate}
              onChange={(e) => setExpireDate(e.target.value)}
              slotProps={{ htmlInput: { min: minExpiry } }}
            />
          </Box>
        </Box>

        <Box
          sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "8px" }}
        >
          <Button
            onClick={onClose}
            disableElevation
            sx={{
              ...BTN_BASE,
              bgcolor: "white",
              color: "var(--color-rodeo-black)",
              border: "1px solid var(--color-smoke)",
              "&:hover": { bgcolor: "white", borderColor: "var(--color-dark-dust)" },
            }}
          >
            {RESUME_MODAL.dismiss}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            disableElevation
            sx={{
              ...BTN_BASE,
              bgcolor: "var(--color-rodeo-black)",
              color: "white",
              "&:hover": { bgcolor: "#222" },
              "&.Mui-disabled": { bgcolor: "var(--color-smoke)", color: "white" },
            }}
          >
            {RESUME_MODAL.confirm}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
